import React, { useEffect, useMemo } from "react";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { releaseFocusFromBackground } from "../utils/modalFocus";
import {
  Box,
  Dialog,
  Slide,
  Paper,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import PeopleIcon from "@mui/icons-material/People";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { lightTheme } from "../styles/theme";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import { TransitionProps } from "@mui/material/transitions";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { USER_MANAGEMENT, VIEW_NOTICES, TEAM_MANAGEMENT } from "../routes";
import { getLibraryNavPath } from "../utils/libraryNav";

interface MobileNavSheetProps {
  open: boolean;
  onClose: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="right" ref={ref} {...props} />;
});

const MobileNavSheet: React.FC<MobileNavSheetProps> = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    isUserManagementEnabled,
    isCircleManagementEnabled,
    isTeamManagementEnabled,
    isAiEnabled,
  } = useFeatureFlags();
  const isSchoolOrg =
    typeof user?.schoolType === "string"
      ? user.schoolType.toLowerCase().trim() === "school"
      : false;
  const isChurchOrg =
    typeof user?.schoolType === "string"
      ? user.schoolType.toLowerCase().trim() === "church"
      : false;
  const isOrganizationOrg =
    typeof user?.schoolType === "string"
      ? user.schoolType.toLowerCase().trim() === "organization"
      : false;

  useBodyScrollLock(open);

  useEffect(() => {
    if (open) {
      releaseFocusFromBackground();
    }
  }, [open]);

  const shortcuts = useMemo(() => {
    // Check if user has access to Team Management (Admin or Owner only)
    const canManageTeamByRole =
      user?.userType === "team"
        ? user?.accessLevel === "Admin" || user?.accessLevel === "Owner"
        : user?.isSchoolApproved; // Org admins have access
    const canManageTeam = canManageTeamByRole && isTeamManagementEnabled;

    const baseShortcuts = [
      {
        label: "Home",
        icon: <HomeIcon color="primary" sx={{ fontSize: 40 }} />,
        action: () => navigate("/"),
      },
      {
        label: "Create",
        icon: (
          <AddCircleIcon
            sx={{ color: theme.palette.info.main, fontSize: 40 }}
          />
        ),
        action: () => {
          onClose();
          navigate("/create-notice");
        },
      },
      {
        label: "Feed",
        icon: (
          <NotificationsActiveIcon
            sx={{ color: theme.palette.custom.navNoticeFeed, fontSize: 40 }}
          />
        ),
        action: () => navigate(VIEW_NOTICES),
      },
      ...(isCircleManagementEnabled
        ? [
            {
              label: "Circles",
              icon: (
                <GroupWorkIcon
                  sx={{ color: theme.palette.custom.navManageCircles, fontSize: 40 }}
                />
              ),
              action: () => navigate("/circles"),
            },
          ]
        : []),
      ...(isUserManagementEnabled
        ? [
            {
              label: "Users",
              icon: (
                <PeopleIcon sx={{ color: theme.palette.info.main, fontSize: 40 }} />
              ),
              action: () => navigate(USER_MANAGEMENT),
            },
          ]
        : []),
    ];

    // Add Team Management if user has access
    if (canManageTeam) {
      baseShortcuts.push({
        label: "Team",
        icon: (
          <AdminPanelSettingsIcon
            sx={{ color: theme.palette.custom.navTeamManagement, fontSize: 40 }}
          />
        ),
        action: () => navigate(TEAM_MANAGEMENT),
      });
    }

    baseShortcuts.push({
      label: "Calendar",
      icon: (
        <EventNoteIcon
          sx={{ color: theme.palette.warning.main, fontSize: 40 }}
        />
      ),
      action: () => navigate("/calendar"),
    });
    baseShortcuts.push({
      label: "Drive",
      icon: (
        <FolderSharedIcon
          sx={{ color: theme.palette.colors.teal, fontSize: 40 }}
        />
      ),
      action: () => navigate("/drive"),
    });
    const showLibraryShortcut =
      (isSchoolOrg && isAiEnabled) || isChurchOrg || isOrganizationOrg;
    if (showLibraryShortcut) {
      baseShortcuts.push({
        label: "Library",
        icon: (
          <AutoStoriesIcon
            sx={{ color: theme.palette.custom.navTeamManagement, fontSize: 40 }}
          />
        ),
        action: () => navigate(getLibraryNavPath(user)),
      });
    }

    return baseShortcuts;
  }, [
    navigate,
    onClose,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.custom.navNoticeFeed,
    theme.palette.custom.navManageCircles,
    theme.palette.custom.navTeamManagement,
    theme.palette.colors.teal,
    isUserManagementEnabled,
    isCircleManagementEnabled,
    isTeamManagementEnabled,
    isAiEnabled,
    isSchoolOrg,
    isChurchOrg,
    isOrganizationOrg,
    user,
  ]);

  const handleAction = (fn: () => void) => {
    fn();
    onClose();
  };

  const seeMoreSections = [
    {
      label: "Help & Support",
      icon: <HelpOutlineIcon />,
      action: () => navigate("/help/videos"),
    },
    {
      label: "Settings & Privacy",
      icon: <SettingsIcon />,
      action: () => navigate("/profile"),
    },
    {
      label: "Logout",
      icon: <LogoutIcon color="error" />,
      action: () => logout(),
    },
  ];

  const name =
    user?.fullName ||
    user?.schoolName ||
    `${user?.firstName || "User"} ${user?.lastName || ""}`;

  return (
    <Dialog
      fullScreen
      open={open}
      TransitionComponent={Transition}
      onClose={onClose}
      disableRestoreFocus
      disableScrollLock
    >
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: theme.palette.primary.main,
              fontSize: "1.5rem",
            }}
          >
            Noticeboard
          </Typography>
          <IconButton autoFocus onClick={onClose} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: `0 8px 30px ${lightTheme.colors.shadowLight}`,
              cursor: "pointer",
            }}
            onClick={() => {
              onClose();
              navigate("/profile");
            }}
          >
            <Avatar
              src={
                typeof user?.avatar === "string"
                  ? (user?.avatar as string)
                  : typeof (user?.schoolLogo as string) === "string"
                  ? (user?.schoolLogo as string)
                  : undefined
              }
              alt={name}
              sx={{
                width: 48,
                height: 48,
                bgcolor: theme.palette.primary.main,
                fontSize: 20,
                fontWeight: 600,
              }}
            >
              {name
                ?.split(" ")
                .map((part) => part.charAt(0))
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600 }}>
                {name || "Welcome"}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {user?.email || user?.schoolName}
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ px: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}
          >
            Your shortcuts
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1.5,
            }}
          >
            {shortcuts.map((shortcut) => (
              <Paper
                key={shortcut.label}
                elevation={0}
                onClick={() => handleAction(shortcut.action)}
                sx={{
                  height: 100,
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.2s ease",
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                }}
              >
                {shortcut.icon}
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, textAlign: "center" }}
                >
                  {shortcut.label}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        <Box sx={{ px: 3, mt: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}
          >
            More
          </Typography>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              backgroundColor: "background.paper",
              mb: { xs: 3, sm: 4 },
            }}
          >
            <List disablePadding sx={{ backgroundColor: "background.paper", pb: 1 }}>
              {seeMoreSections.map((section, idx) => (
                <React.Fragment key={section.label}>
                  <ListItemButton
                    onClick={() => handleAction(section.action)}
                    sx={{ backgroundColor: "background.paper" }}
                  >
                    <ListItemIcon>{section.icon}</ListItemIcon>
                    <ListItemText primary={section.label} />
                  </ListItemButton>
                  {idx < seeMoreSections.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MobileNavSheet;
